import { ShoppingCartOutlined } from '@ant-design/icons';
import {
  PagamentoWithDettaglioDTO,
  usePostFrontRichiesteIdPagamentiIdPagamentoOnline,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { EnumCodStatoPagamento } from '@framework/frontoffice-submission/src/burden/payments/EnumCodStatoPagamento';
import { useTranslation } from '@framework/i18n';
import { Button, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { isPaymentLockedToDifferentMethod } from './isPaymentLockedToMethod';
import { generatePaymentCallbackUrl } from './StartStampDutyPayment';

interface StartPaymentButtonProps {
  payment: PagamentoWithDettaglioDTO;
}

export function StartPaymentButton(props: StartPaymentButtonProps) {
  const { payment } = props;

  const { t } = useTranslation();

  const { mutateAsync: startPayment, isLoading } =
    usePostFrontRichiesteIdPagamentiIdPagamentoOnline({
      mutation: {
        onSuccess: response => {
          message.info(
            t('start-payment-button.avvio-pagamento', `Avvio del pagamento...`),
          );
          console.log(`[startPayment.onSuccess`, { response });
        },
      },
    });

  const handleClick = useCallback(async () => {
    try {
      const redirectUrl = await startPayment({
        id: payment.idRichiesta!,
        idPagamento: payment.id!,
        data: {
          urlRitorno: generatePaymentCallbackUrl(),
        },
      });

      // Se è stato restituito un URL di redirect, reindirizzo l'utente
      if (redirectUrl && typeof redirectUrl === 'string') {
        window.location.href = redirectUrl;
      } else {
        console.warn(`[StartPaymentButton] startPayment() returned no redirect URL`, { redirectUrl }); // prettier-ignore
        message.error(
          t(
            'start-payment-button.errore-avvio-pagamento',
            `Si è verificato un errore durante l'avvio del pagamento`,
          ),
        );
      }
    } catch (e) {
      console.error(`[StartPaymentButton] startPayment() failed`, { e });
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'start-payment-button.errore-avvio-pagamento',
            `Si è verificato un errore durante l'avvio del pagamento`,
          ),
        ),
      );
    }
  }, [payment.id, payment.idRichiesta, startPayment, t]);

  if (isPaymentLockedToDifferentMethod(payment, 'ONLINE')) return null;

  if (payment.codStatoPagamento === EnumCodStatoPagamento.SUCCESSO) return null;

  // Blocchiamo i pagamenti in errore poiché GovPay non permette di ripetere i pagamenti
  if (payment.codStatoPagamento === EnumCodStatoPagamento.FALLIMENTO)
    return null;

  if (!hasPayment(payment)) return null;

  return (
    <Tooltip
      title={t('form.procedi-al-pagamento', 'Procedi al pagamento online')}
    >
      <Button
        type="primary"
        icon={
          <ShoppingCartOutlined
            aria-label={t(
              'form.procedi-al-pagamento-link-esterno',
              'Procedi al pagamento (apre un link esterno)',
            )}
          />
        }
        onClick={handleClick}
        disabled={!payment.id}
        loading={
          isLoading ||
          payment.codStatoPagamento === EnumCodStatoPagamento.PENDENTE
        }
      />
    </Tooltip>
  );
}

function hasPayment(payment: PagamentoWithDettaglioDTO): boolean {
  const hasBurdenPayment =
    payment.oneri?.some(o => o.tipoOnere?.flPagamentoModello1) ?? false;
  const hasQuotePayment =
    payment.preventivo?.tipoPagamento?.flPagamentoModello1 ?? false;
  return hasBurdenPayment || hasQuotePayment;
}
